"use client";

import React from "react";
import { QueryClientProvider } from "@tanstack/react-query";
import { createQueryClient } from "@/lib/query-client";
import { AuthProvider } from "@/hooks/use-auth";
import { ToastProvider } from "@/components/ui/toaster";
import { captureException } from "@/lib/sentry";

const Providers = ({ children }: { children: React.ReactNode }) => {
  const [client] = React.useState(() => createQueryClient());

  React.useEffect(() => {
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      captureException(event.reason, (scope) => {
        scope.setTag?.("origin", "window.unhandledrejection");
      });
    };

    const handleWindowError = (event: ErrorEvent) => {
      captureException(event.error ?? event.message, (scope) => {
        scope.setTag?.("origin", "window.error");
      });
    };

    window.addEventListener("unhandledrejection", handleUnhandledRejection);
    window.addEventListener("error", handleWindowError);

    return () => {
      window.removeEventListener("unhandledrejection", handleUnhandledRejection);
      window.removeEventListener("error", handleWindowError);
    };
  }, []);

  return (
    <QueryClientProvider client={client}>
      <ToastProvider>
        <AuthProvider>{children}</AuthProvider>
      </ToastProvider>
    </QueryClientProvider>
  );
};

export default Providers;
